<template>
  <div class="sm:c-col-4 md:c-col-8 lg:c-col-6 p-cadastro-rapido-container">
    <p-form
      ref="form"
      @submit="onFormSubmit"
      v-slot="{ invalid }"
    >
      <!-- Título -->
      <div class="c-row">
        <div class="c-col">
          <h3 class="c-text-h3 ui-mb-6">
            Parece que você é novo no Estratégia
          </h3>
        </div>
      </div>

      <!-- Subtítulo -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <div class="c-text-b1 c-text-gray-400">
            Vamos criar uma conta. Para isso, informe os seguintes dados:
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|email"
          name="Email"
          v-slot="{ errors }"
        >
          <p-input
            v-model="email"
            data-cy="emailField"
            label="Email"
            type="email"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Nome completo -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required"
          name="Nome completo"
          v-slot="{ errors }"
        >
          <p-input
            v-model="name"
            data-cy="nameField"
            label="Nome completo"
            type="text"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Telefone -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|length:11"
          :custom-messages="{ length: 'Celular inválido' }"
          name="Celular"
          v-slot="{ errors }"
        >
          <p-input
            v-model="phone"
            data-cy="phoneField"
            label="Celular*"
            mask="(00) 0 0000-0000"
            hint="DDD + nº do celular"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Senha -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|min:6"
          name="Senha"
          v-slot="{ errors }"
        >
          <input-password
            v-model="password"
            data-cy="passwordField"
            label="Senha"
            name="passwordField"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Botão Prosseguir -->
      <div class="c-row ui-mb-6">
        <div class="c-col">
          <c-button
            data-cy="submitButton"
            size="lg"
            type="submit"
            class="ui-w-full"
            :disabled="invalid"
            :loading="isFormSending"
          >
            Prosseguir com criação de conta
          </c-button>
        </div>
      </div>

      <!-- Social Login -->
      <div
        v-if="showSocialLogin"
        class="c-row ui-mb-4"
      >
        <div class="c-col">
          <separator />
        </div>
        <div class="c-d-flex">
          <social-login-button
            provider="facebook"
            @logged-in="handleLoggedIn"
          />
          <social-login-button
            provider="google"
            @logged-in="handleLoggedIn"
          />
          <social-login-button
            provider="apple"
            @logged-in="handleLoggedIn"
          />
        </div>
        <div class="c-col">
          <separator />
        </div>
      </div>
    </p-form>

    <!-- Divisor "Ou" -->
    <div class="login-divider ui-my-6">
      <div class="login-divider__line" />
      <span class="login-divider__text c-text-b2">Ou</span>
      <div class="login-divider__line" />
    </div>

    <!-- Já é aluno? -->
    <div class="c-row ui-mt-8">
      <div class="c-col">
        <div class="ui-flex ui-flex-col c-text-gray">
          <span class="c-text-b1 c-text-gray-500 ui-mb-2"><strong>Já é aluno?</strong></span>
          <template v-if="$slots.login">
            <slot name="login" />
          </template>
          <router-link
            v-else
            :to="{ name: 'login', query: { ...$route.query } }"
            data-cy="loginLink"
            class="width-fit"
          >
            <c-link class="padding-remover">
              Faça login com outro email
            </c-link>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CButton, CLink } from '@estrategiahq/coruja-web-ui';
import PForm from '@/components/p-form';
import PInput from '@/components/p-input';
import Separator from '@/components/separator';
import SocialLoginButton from '@/components/p-social-login-button';
import { ValidationProvider } from 'vee-validate';
import { userAlreadyExistsError } from '@/utils/errors';
import InputPassword from '@/components/input-password';

export default {
  name: 'PCadastroRapido',
  props: {
    preventNavigation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CButton,
    CLink,
    InputPassword,
    PForm,
    PInput,
    Separator,
    SocialLoginButton,
    ValidationProvider,
  },
  data: () => ({
    email: '',
    name: '',
    phone: '',
    password: '',
    isFormSending: false,
    showSocialLogin: false,
    errors: [],
  }),
  mounted() {
    this.email = this.$route.query.email?.trim() ?? '';
    this.$socialLogin.someActive().then((someActive) => {
      this.showSocialLogin = someActive;
    });
  },
  methods: {
    getBoolParam(name) {
      try {
        const val = JSON.parse(this.$route.query[name]);
        return !!val;
      } catch (_) {
        return false;
      }
    },
    async onFormSubmit() {
      this.isFormSending = true;
      try {
        await this.$services.accounts.fastRegistration(this.email, this.name, this.phone, this.password);
        if (this.preventNavigation) {
          this.$emit('registered', { email: this.email });
        } else {
          this.$router.push({ name: 'confirme-seu-codigo', query: { ...this.$route.query, email: this.email } });
        }
      } catch (error) {
        this.errors = error.response?.data?.errors || [];
        this.$toast.show({
          type: 'error',
          text: 'Erro ao realizar o cadastro',
        });

        if (error === userAlreadyExistsError) {
          this.$refs.form.setErrors({
            email: ['Email já cadastrado'],
          });
        }
      } finally {
        this.isFormSending = false;
      }
    },
    handleLoggedIn(val) {
      this.$emit('logged-in', val);
    },
  },
};
</script>

<style scoped>
.p-cadastro-rapido-container {
  padding: 2rem !important;
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-divider__line {
  flex: 1;
  height: 1px;
  background: #ccc;
  opacity: 0.48;
}

.login-divider__text {
  color: #9e9e9e;
  white-space: nowrap;
}

.padding-remover {
  padding: 0 !important;
  border: none !important;
}

.width-fit {
  width: fit-content !important;
}
</style>
